import type { Environment, EnvironmentServices, ServiceStatus } from '@central/api';
import {
  Card,
  formatAgo,
  formatDateTime,
  formatDuration,
  HealthChip,
  InfoRow,
  StatusChip,
} from '@central/ui';
import { Table } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import type { CSSProperties, ReactNode } from 'react';
import { keys, useApi } from '../api';
import { LineChart24h } from '../components/LineChart24h';
import { DataTable, Empty, QueryState } from '../components/parts';
import { formatCpuMilli, hourlyPoints, useServices } from '../lib/environment';
import { formatBytes } from '../lib/format';
import { mono, muted } from '../lib/labels';

/** Cluster CPU and memory over the last 24 hours, from the hourly rollups. */
export function Metrics({ e }: { e: Environment }) {
  return <MetricsCharts environmentId={e.id} summary={e.summary} />;
}

/**
 * Cluster CPU and memory over the 24 hours before `end` (default now), from
 * the hourly rollups; `summary` adds the latest values to a chart of now.
 */
export function MetricsCharts({
  environmentId,
  summary: s,
  end,
}: {
  environmentId: string;
  summary?: Environment['summary'];
  /** RFC 3339; the charts end here instead of now. */
  end?: string;
}) {
  const api = useApi();
  const metrics = useQuery({
    queryKey: end
      ? keys.environmentMetricsTo(environmentId, end)
      : keys.environmentMetrics(environmentId),
    queryFn: () => api.environments.metrics(environmentId, end ? { end } : {}),
  });
  const until = end ? new Date(end) : undefined;
  const span = end ? '24 hours before the crash' : 'last 24 hours';
  const memTotal = metrics.data?.memTotalBytes ?? s?.memTotalBytes;
  const cpu = hourlyPoints(metrics.data, 'cpuPercent');
  const mem = hourlyPoints(metrics.data, 'memUsedBytes');
  const now = (text: string) => <span style={{ fontSize: 13, ...muted }}>now {text}</span>;
  const body = (points: typeof cpu, chart: ReactNode) => (
    <QueryState isPending={metrics.isPending} error={metrics.error}>
      {() =>
        points.length === 0 ? (
          <span style={{ fontSize: 14, ...muted }}>No hourly data yet.</span>
        ) : (
          chart
        )
      }
    </QueryState>
  );
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      <Card
        title="Cluster CPU · 24 h"
        action={s && now(`${s.cpuPercent.toFixed(0)}%`)}
        style={{ flex: '1 1 300px', minWidth: 0 }}
      >
        {body(
          cpu,
          <LineChart24h points={cpu} max={100} label={`Cluster CPU %, ${span}`} now={until} />,
        )}
      </Card>
      <Card
        title="Cluster memory · 24 h"
        action={s && now(`${formatBytes(s.memUsedBytes)} / ${formatBytes(s.memTotalBytes)}`)}
        style={{ flex: '1 1 300px', minWidth: 0 }}
      >
        {body(
          mem,
          <LineChart24h
            points={mem}
            max={memTotal || Math.max(...mem.map((p) => p.value), 1)}
            label={`Cluster memory used, ${span}`}
            now={until}
          />,
        )}
      </Card>
    </div>
  );
}

/** The services of the latest report kept, or of the latest at or before `at`. */
export function Services({
  environmentId,
  at,
  style,
}: {
  environmentId: string;
  /** RFC 3339: the report at or before this time. */
  at?: string;
  style?: CSSProperties;
}) {
  const services = useServices(environmentId, at);
  const items = services.data?.items ?? [];
  const restarts = items.some((v) => v.restarts24h !== undefined);
  const licensed = items.filter((v) => v.licensed !== false);
  const unlicensed = items.filter((v) => v.licensed === false);
  const head = ['Service', 'Status', 'Ready', 'CPU', 'Memory', 'Version'];
  if (restarts) head.push('Restarts · 24 h');
  const row = (v: ServiceStatus) => (
    <Table.Tr key={v.name} style={v.licensed === false ? muted : undefined}>
      <Table.Td style={{ ...mono, fontWeight: 500 }}>{v.name}</Table.Td>
      <Table.Td>
        <HealthChip state={v.state} />
      </Table.Td>
      <Table.Td style={mono}>
        {v.ready} / {v.desired}
      </Table.Td>
      <Table.Td style={mono}>{formatCpuMilli(v.cpuMilli)}</Table.Td>
      <Table.Td style={mono}>{formatBytes(v.memBytes)}</Table.Td>
      <Table.Td style={mono}>{v.version ?? '–'}</Table.Td>
      {restarts && <Table.Td style={mono}>{v.restarts24h ?? '–'}</Table.Td>}
    </Table.Tr>
  );
  return (
    <Card
      title="Services"
      action={
        services.data?.at && (
          <span style={{ fontSize: 13, ...muted }}>
            reported {at ? formatDateTime(services.data.at) : formatAgo(services.data.at)}
          </span>
        )
      }
      padded={false}
      style={style}
    >
      <QueryState isPending={services.isPending} error={services.error}>
        {() =>
          items.length === 0 ? (
            <Empty>
              {at ? 'No report kept from that time.' : 'No report received in the last 7 days.'}
            </Empty>
          ) : (
            <DataTable label="Services" head={head}>
              {licensed.map(row)}
              {unlicensed.length > 0 && (
                <Table.Tr>
                  <Table.Td
                    colSpan={head.length}
                    style={{ fontSize: 12, fontWeight: 600, ...muted }}
                  >
                    Not licensed
                  </Table.Td>
                </Table.Tr>
              )}
              {unlicensed.map(row)}
            </DataTable>
          )
        }
      </QueryState>
    </Card>
  );
}

/** The nodes of the report `useServices` reads for `at`. */
export function Nodes({ environmentId, at }: { environmentId: string; at?: string }) {
  const services = useServices(environmentId, at);
  const nodes = services.data?.nodes ?? [];
  const os = nodes.some((n) => n.os !== undefined);
  const role = nodes.some((n) => n.role !== undefined);
  const cores = nodes.some((n) => n.cpuCores !== undefined);
  const head = ['Node', 'Ready', 'CPU', 'Memory'];
  if (role) head.push('Role');
  if (os) head.push('OS');
  if (cores) head.push('Cores');
  return (
    <Card title="Nodes" padded={false}>
      <QueryState isPending={services.isPending} error={services.error}>
        {() =>
          nodes.length === 0 ? (
            <Empty>No nodes in that report.</Empty>
          ) : (
            <DataTable label="Nodes" head={head}>
              {nodes.map((n) => (
                <Table.Tr key={n.name}>
                  <Table.Td style={{ ...mono, fontWeight: 500 }}>{n.name}</Table.Td>
                  <Table.Td>{n.ready ? 'Ready' : 'Not ready'}</Table.Td>
                  <Table.Td style={mono}>{n.cpuPercent.toFixed(0)}%</Table.Td>
                  <Table.Td style={mono}>
                    {formatBytes(n.memUsedBytes)} / {formatBytes(n.memTotalBytes)}
                  </Table.Td>
                  {role && <Table.Td>{n.role ?? '–'}</Table.Td>}
                  {os && <Table.Td>{n.os ?? '–'}</Table.Td>}
                  {cores && <Table.Td style={mono}>{n.cpuCores ?? '–'}</Table.Td>}
                </Table.Tr>
              ))}
            </DataTable>
          )
        }
      </QueryState>
    </Card>
  );
}

const notReported = 'Not reported by this version.';

/**
 * Databases, API, nodes and communication agents of the latest report.
 * Nothing renders before the first report; a version that does not send a
 * section shows it as not reported.
 */
export function FleetDetail({ e }: { e: Environment }) {
  const services = useServices(e.id);
  const data = services.data;
  if (!data?.at) return null;
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
        <Databases data={data} />
        <ApiCard data={data} />
      </div>
      <Nodes environmentId={e.id} />
      <Agents data={data} e={e} />
    </>
  );
}

function Databases({ data }: { data: EnvironmentServices }) {
  const dbs = data.databases;
  const backlog = dbs?.some((d) => d.backlog !== undefined);
  const head = ['Database', 'Status', 'Ping', 'Connections', 'Uptime'];
  if (backlog) head.push('Backlog');
  return (
    <Card title="Databases" padded={false} style={{ flex: '2 1 480px' }}>
      {!dbs ? (
        <Empty>{notReported}</Empty>
      ) : (
        <DataTable label="Databases" head={head} minWidth={520}>
          {dbs.map((d) => (
            <Table.Tr key={d.name}>
              <Table.Td style={{ ...mono, fontWeight: 500 }}>{d.name}</Table.Td>
              <Table.Td>
                <HealthChip state={d.state} />
              </Table.Td>
              <Table.Td style={mono}>{d.pingMs !== undefined ? `${d.pingMs} ms` : '–'}</Table.Td>
              <Table.Td style={mono}>{d.connections ?? '–'}</Table.Td>
              <Table.Td style={mono}>
                {d.uptimeSeconds !== undefined ? formatDuration(d.uptimeSeconds) : '–'}
              </Table.Td>
              {backlog && (
                <Table.Td style={mono}>{d.backlog?.toLocaleString('en-US') ?? '–'}</Table.Td>
              )}
            </Table.Tr>
          ))}
        </DataTable>
      )}
    </Card>
  );
}

function ApiCard({ data }: { data: EnvironmentServices }) {
  const a = data.api;
  return (
    <Card title="API" style={{ flex: '1 1 240px' }}>
      {!a ? (
        <span style={{ fontSize: 14, ...muted }}>{notReported}</span>
      ) : (
        <>
          {a.p50Ms !== undefined && (
            <InfoRow label="Latency p50">
              <span style={mono}>{a.p50Ms} ms</span>
            </InfoRow>
          )}
          {a.p95Ms !== undefined && (
            <InfoRow label="Latency p95">
              <span style={mono}>{a.p95Ms} ms</span>
            </InfoRow>
          )}
          {a.openConnections !== undefined && (
            <InfoRow label="Open connections">
              <span style={mono}>{a.openConnections}</span>
            </InfoRow>
          )}
        </>
      )}
    </Card>
  );
}

function Agents({ data, e }: { data: EnvironmentServices; e: Environment }) {
  const agents = data.agents;
  const customer = new Map(e.tenants.map((t) => [t.tenantId, t.customerName]));
  const connected = agents?.filter((a) => a.connected).length ?? 0;
  return (
    <Card
      title="Communication agents"
      action={
        agents &&
        agents.length > 0 && (
          <span style={{ fontSize: 13, ...muted }}>
            {connected} of {agents.length} connected
          </span>
        )
      }
      padded={false}
    >
      {!agents ? (
        <Empty>{notReported}</Empty>
      ) : agents.length === 0 ? (
        <Empty>No communication agents.</Empty>
      ) : (
        <>
          <DataTable
            label="Communication agents"
            head={['Agent', 'Customer', 'Status', 'Version', 'OS', 'Last check-in']}
          >
            {agents.map((a) => (
              <Table.Tr key={a.id}>
                <Table.Td style={{ fontWeight: 500 }}>
                  {a.name || a.id}
                  {a.name && a.name !== a.id && (
                    <div style={{ ...mono, fontSize: 12, ...muted }}>{a.id}</div>
                  )}
                </Table.Td>
                <Table.Td>
                  {!a.tenantId ? (
                    <span style={muted}>No tenant</span>
                  ) : (
                    (customer.get(a.tenantId) ?? <span style={mono}>{a.tenantId}</span>)
                  )}
                </Table.Td>
                <Table.Td>
                  {a.connected ? (
                    <StatusChip tone="ok">Connected</StatusChip>
                  ) : (
                    <StatusChip tone="down">Disconnected</StatusChip>
                  )}
                </Table.Td>
                <Table.Td style={mono}>{a.version ?? '–'}</Table.Td>
                <Table.Td>{a.os ?? '–'}</Table.Td>
                <Table.Td>{a.lastCheckInAt ? formatAgo(a.lastCheckInAt) : '–'}</Table.Td>
              </Table.Tr>
            ))}
          </DataTable>
          {data.agentsTruncated && (
            <p style={{ margin: 0, padding: '10px 16px', fontSize: 13, ...muted }}>
              The installation has more agents than it reports; this list is truncated.
            </p>
          )}
        </>
      )}
    </Card>
  );
}
